import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function Relationship() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const card1Reveal = useScrollReveal({ threshold: 0.2 });
  const card2Reveal = useScrollReveal({ threshold: 0.2 });
  const card3Reveal = useScrollReveal({ threshold: 0.2 });
  const highlightReveal = useScrollReveal({ threshold: 0.2 });

  return (
    <section className="py-32 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-200/30 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-20 ${headerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            💝 SANTYKIAI
          </div>
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
            Kaip Knyga Padės Jūsų Santykiams
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Suprasti save – tai pirmasis žingsnis link gilesnių ir autentiškesnių santykių su kitais
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {/* Card 1 */}
          <div 
            ref={card1Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card1Reveal.isVisible ? 'animate-fade-in-up stagger-1' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-heart-3-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 relative">
              Suprasti Partnerį
            </h3>
            <p className="text-gray-600 leading-relaxed relative">
              Sužinosite, kaip skirtingi žmonių tipai mato pasaulį ir ko jiems reikia santykiuose. Tai padės geriau suprasti partnerio poreikius ir elgesį.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>

          {/* Card 2 */}
          <div 
            ref={card2Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card2Reveal.isVisible ? 'animate-fade-in-up stagger-2' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-chat-heart-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 relative">
              Geresnė Komunikacija
            </h3>
            <p className="text-gray-600 leading-relaxed relative">
              Išmoksite kalbėti taip, kad kitas žmogus jus išgirstų. Suprasite, kodėl kartais nesusikalbate, nors atrodo, kad kalbate apie tą patį.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>

          {/* Card 3 */}
          <div 
            ref={card3Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card3Reveal.isVisible ? 'animate-fade-in-up stagger-3' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 relative">
              Autentiški Santykiai
            </h3>
            <p className="text-gray-600 leading-relaxed relative">
              Kai suprantate save, galite būti autentiški santykiuose. Nebereikia dėvėti kaukių ar vaidinti – galite būti savimi ir pritraukti tinkamus žmones.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>
        </div>

        {/* Bottom Highlight Section */}
        <div 
          ref={highlightReveal.ref}
          className={`relative ${highlightReveal.isVisible ? 'animate-scale-in' : 'opacity-0'}`}
        >
          <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
            {/* Animated sparkle effects */}
            <div className="absolute top-10 left-10 w-3 h-3 bg-white rounded-full animate-sparkle"></div>
            <div className="absolute top-20 right-20 w-2 h-2 bg-white rounded-full animate-sparkle" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-10 left-1/4 w-2 h-2 bg-white rounded-full animate-sparkle" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-20 right-1/3 w-3 h-3 bg-white rounded-full animate-sparkle" style={{ animationDelay: '1.5s' }}></div>

            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>

            <div className="relative z-10">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-semibold mb-6">
                ✨ SVARBIAUSIAS ĮŽVALGA
              </div>
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Santykiai Prasideda Nuo Savęs
              </h3>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Negalite tikėtis sveikų santykių su kitais, jei neturite sveikų santykių su savimi. 
                Ši knyga padės jums pradėti tą kelionę.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
